/**
 * @author        yewon@zumper.com (Yewon Lee)
 * @copyright     Copyright (c) 2020, Zumper
 * @description   getInitialFormValues
 */

import { getMoveInDateFromMoveInText } from '@zcode-ui/web-zumper-ui'
import type { SerializableDate, Unit, User } from '@zcode/bizos'
import type * as MessageC from '@zcode/nr/es/shared/constants/message'
import type {
  FloorplanListing,
  Listable,
  NormalFloorplanListingData,
  Property,
} from '@zcode/state-type'
import { moveInTextValidator } from '../../../../packages/Inputs/validator/moveIn'
import { getCustomMessagesByIntent } from './getCustomMessagesByIntent'
import { getDateValue } from './getDateValue'
import { getSelectedFloorplanId } from './getSelectedFloorplanId'
import type { Values } from './getValidationSchema'

interface Props {
  entity: Listable | Property
  filtersBedroom: readonly boolean[]
  intent:
    | typeof MessageC.APPLY
    | typeof MessageC.MESSAGE
    | typeof MessageC.TOUR
    | typeof MessageC.WAITLIST
    | undefined
  now: number
  selectedFloorplan: FloorplanListing<NormalFloorplanListingData> | undefined
  selectedTime: SerializableDate | null
  selectedUnit: Unit | null | undefined
  user: User
}

export const getInitialFormValues = ({
  entity,
  filtersBedroom,
  intent,
  now,
  selectedFloorplan,
  user,
  selectedTime,
  selectedUnit,
}: Props): Values => {
  const tProfile = user.tenantProfile
  const date = getDateValue(tProfile?.moveInDate)
  const { headerText, listingId, buildingId } = entity || {}
  let moveInText = tProfile?._data.move_in_text
  let moveIn = date || ''
  // see also similar logic in getRefreshedMoveInDate; probably need to update both together

  if (!moveIn && !moveInText) {
    moveInText = 'ASAP'
  }
  if (moveInText && moveInTextValidator.isValidSync(moveInText) && !moveIn) {
    moveIn = getDateValue(getMoveInDateFromMoveInText(moveInText, now)) || moveIn
  }
  return {
    email: user.email || '',
    messageModified: false,
    moveIn,
    moveInText,
    msg:
      listingId || buildingId
        ? getCustomMessagesByIntent({
            intent,
            listingName: headerText,
          })
        : '',
    name: user.fullName || '',
    phone: user.phone || '',
    selectedDateTime: selectedTime && {
      selectedDate: selectedTime.toDateString(),
      selectedTime: selectedTime,
    },
    selectedFloorplanId: getSelectedFloorplanId(entity, selectedFloorplan, filtersBedroom),
    selectedFloorplanBedroom: null,
    selectedFloorplanMaxPrice: null,
    selectedFloorplanMinPrice: null,
    saveSearch: true,
    unitId: selectedUnit?.unitId,
  } as const satisfies Values
}
